import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import { Whitelisting } from '@img-arena/consoles-api-streaming'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FormikTextField } from '@/components/inputs/FormikTextField'
import { SelectBox } from '@/components/inputs/SelectBox'
import { ApiError } from '@/service/ApiError'
import { streamingRestApi } from '@/service/streamingApi'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { getFormError, onFieldChange } from '@/utils/forms/formik'

import {
  WHITELISTING_CLASSIFICATIONS,
  whitelistingFormValidationSchema
} from './whitelistingFrom.validation'

const WhitelistingForm = ({ onClose, onSubmit, open = false }: FormDialogProps<Whitelisting>) => {
  const { enqueueSnackbar } = useSnackbar()

  const formik = useFormik({
    initialValues: {
      IPRange: '',
      classification: null,
      description: ''
    },
    validationSchema: whitelistingFormValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!values.IPRange || !values.classification || !values.description) return

      try {
        setSubmitting(true)

        await streamingRestApi.endpoints.whitelist.addWhitelistIP({
          cidrRange: values.IPRange,
          classification: values.classification,
          comment: values.description,
          type: 'WHITE'
        })

        onSubmit?.()
        enqueueSnackbar(`Successfully whitelisted IP: ${values.IPRange}`, { variant: 'success' })
        setSubmitting(false)
        handleClose()
      } catch (e) {
        const errorMessage = ['Failed to whitelist IP', (e as unknown as ApiError)?.message]
          .filter(Boolean)
          .join(': ')
        enqueueSnackbar(errorMessage, { variant: 'error' })
        setSubmitting(false)
        // Throw the error again to be caught by dialog and doesn't close it
        throw errorMessage
      }
    }
  })

  const handleClose = () => {
    formik.resetForm()
    onClose?.()
  }

  return (
    <ConsoleDialog
      open={open}
      title="Add Subscriber"
      onClose={handleClose}
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <FormikTextField
        label="IP Range"
        placeholder="e.g. 255.255.255.255/32"
        required
        name="IPRange"
        formik={formik}
      />
      <SelectBox
        label="Classification"
        name="classification"
        required
        fullWidth
        value={formik.values.classification}
        onChange={(value) => onFieldChange(formik, 'classification', value)}
        options={WHITELISTING_CLASSIFICATIONS}
        error={getFormError(formik, 'classification')}
      />
      <FormikTextField label="Description" required name="description" formik={formik} />
    </ConsoleDialog>
  )
}

export default WhitelistingForm
